/**
 * Emojis de SuCom por clase CSS (como PromoDescuentos: `<i class="emoji
 * emoji--type-worried">`). El HTML de un comentario solo lleva la clase; la
 * URL del asset vive en una hoja de estilo que el layout inyecta una sola vez
 * en el <head>, así el enlace a la imagen no aparece en cada emoji.
 *
 * Compartido entre servidor (layout genera el CSS) y cliente (componentes
 * que pintan la clase), por eso no importa nada de servidor.
 */
import type { ReactionDefinition } from "./types";

export const EMOJI_BASE_CLASS = "sc-emoji";

/** Clase CSS estable para una reacción (`sc-emoji--smiley`). */
export function reactionEmojiClass(reaction: Pick<ReactionDefinition, "id" | "slug">): string {
  const key = (reaction.slug || reaction.id)
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return `${EMOJI_BASE_CLASS}--${key || reaction.id.replace(/[^a-z0-9_-]/gi, "")}`;
}

/**
 * Solo URLs que no pueden romper el `url("…")` del CSS ni cerrar el <style>:
 * sin comillas, `<`, `>`, `\` ni espacios.
 */
export function safeAssetUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  if (/["'<>\\\s]/.test(url)) return null;
  if (url.startsWith("/uploads/admin/")) return url;
  if (url.startsWith("/api/reactions/asset/")) return url;
  if (/^data:image\/(?:svg\+xml|png|jpeg|webp|gif);base64,[a-z0-9+/=]+$/i.test(url)) return url;
  if (/^https:\/\/\S+$/i.test(url)) return url;
  if (/^http:\/\/localhost(?::\d+)?\/\S+$/i.test(url)) return url;
  return null;
}

/** ¿Esta reacción se pinta con asset propio (y por tanto con clase CSS)? */
export function hasEmojiAsset(reaction: ReactionDefinition): boolean {
  return safeAssetUrl(reaction.assetUrl) !== null;
}

/** Hoja de estilo con una regla por reacción con asset. */
export function buildReactionEmojiCss(list: readonly ReactionDefinition[]): string {
  const rules = [
    // 20×20 (PromoDescuentos usa 24 px). El margen negativo evita que el
    // emoji estire el interlineado del comentario.
    `.${EMOJI_BASE_CLASS}{display:inline-block;width:20px;height:20px;vertical-align:middle;margin-block:-2px;line-height:1;background:center/contain no-repeat;font-style:normal}`,
  ];
  for (const reaction of list) {
    const url = safeAssetUrl(reaction.assetUrl);
    if (!url) continue;
    rules.push(`.${reactionEmojiClass(reaction)}{background-image:url("${url}")}`);
  }
  return rules.join("");
}
